import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  HeartPulse, Radar, Zap, Bell, Camera, Loader2, X, Plus, Check, Trash2,
  Send, RefreshCw, Play, AlertTriangle, TrendingDown, Users, Store, ChevronRight,
  ImageIcon, Timer, Target, Sparkles, Filter, Edit3, Percent
} from "lucide-react";
import FilterMultiSelect, { SearchSelect } from "@/components/filters/FilterMultiSelect";

// ═══════════════════════════════════════════════════════════════════════════
//  DATA HEALTH CHECK
// ═══════════════════════════════════════════════════════════════════════════
const METRIC_LABEL = { ca: "CA", achats: "Achats", rh: "RH", qualite: "Qualité" };

export function DataHealthView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weeks, setWeeks] = useState(4);
  const [filter, setFilter] = useState(""); // filter store name
  const [selected, setSelected] = useState(new Set());
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/data-health?weeks=${weeks}`);
      setData(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Chargement impossible"); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weeks]);

  const filtered = (data?.stores || []).filter(s => !filter.trim() || (s.store_name || "").toLowerCase().includes(filter.toLowerCase()));

  const totalCells = data ? data.totals.cells : 0;
  const okCells = data ? (data.totals.ca + data.totals.achats + data.totals.rh + data.totals.qualite) : 0;
  const healthPct = totalCells > 0 ? Math.round(100 * okCells / (totalCells * 4)) : 0;

  const toggle = (sid) => setSelected(prev => { const n = new Set(prev); n.has(sid) ? n.delete(sid) : n.add(sid); return n; });
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(s => s.store_id)));
  };

  const sendReminders = async () => {
    if (selected.size === 0) return toast.error("Sélectionnez au moins un magasin");
    if (!confirm(`Envoyer une relance aux responsables de ${selected.size} magasin(s) ?`)) return;
    setSending(true);
    try {
      const { data: r } = await api.post("/admin/data-health/remind", { store_ids: [...selected], missing_types: ["ca", "achats", "rh", "qualite"] });
      toast.success(`${r.sent} notification(s) envoyée(s) sur ${r.stores} magasin(s)`);
      setSelected(new Set());
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSending(false);
  };

  if (loading) return <div className="py-20 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin inline"/></div>;
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="data-health-view">
      {/* Health overview */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-white border border-slate-200 rounded-2xl p-4 col-span-2 md:col-span-1">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white grid place-items-center shadow-sm">
            <HeartPulse className="w-5 h-5"/>
          </div>
          <div className="mt-3 text-[10px] font-bold uppercase tracking-widest text-slate-400">Santé globale</div>
          <div className="font-display text-3xl font-bold text-slate-900 mt-0.5">{healthPct}%</div>
          <div className="text-xs text-slate-500 mt-1">{okCells} / {totalCells * 4} cellules remplies</div>
        </div>
        {["ca", "achats", "rh", "qualite"].map(k => {
          const total = totalCells;
          const ok = data.totals[k];
          const pct = total ? Math.round(100 * ok / total) : 0;
          return (
            <div key={k} className="bg-white border border-slate-200 rounded-2xl p-4">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{METRIC_LABEL[k]}</div>
              <div className="font-display text-2xl font-bold text-slate-900 mt-1">{pct}%</div>
              <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div className={`h-full ${pct >= 90 ? "bg-emerald-500" : pct >= 70 ? "bg-amber-500" : "bg-rose-500"}`} style={{width: `${pct}%`}}/>
              </div>
              <div className="text-[10px] text-slate-400 mt-1">{ok}/{total} semaines</div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Filtrer par magasin…"
               className="flex-1 min-w-[200px] px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white"/>
        <SearchSelect value={weeks} onChange={v => setWeeks(Number(v))} options={[{ value: 4, label: "4 dernières semaines" }, { value: 8, label: "8 dernières semaines" }, { value: 12, label: "12 dernières semaines" }]} data-testid="dh-weeks" size="lg" menuWidth={260}/>
        <button onClick={load} className="px-3 py-2 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 text-xs font-semibold flex items-center gap-1">
          <RefreshCw className="w-3.5 h-3.5"/> Actualiser
        </button>
        {selected.size > 0 && (
          <button onClick={sendReminders} disabled={sending} data-testid="dh-remind"
                  className="px-3 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold flex items-center gap-1 disabled:opacity-40">
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Send className="w-3.5 h-3.5"/>}
            Relancer ({selected.size})
          </button>
        )}
      </div>

      {/* Matrix */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-[10px] font-bold tracking-wider text-slate-500 uppercase">
              <th className="px-2 py-2 w-8">
                <input type="checkbox" checked={selected.size > 0 && selected.size === filtered.length} onChange={toggleAll} className="w-3.5 h-3.5"/>
              </th>
              <th className="px-3 py-2 sticky left-0 bg-slate-50 min-w-[200px]">Magasin · BM</th>
              {data.weeks.map(w => (
                <th key={w} colSpan={4} className="px-2 py-2 text-center border-l border-slate-200">{w}</th>
              ))}
            </tr>
            <tr className="text-[9px] font-bold text-slate-400 bg-slate-50/60">
              <th></th><th className="sticky left-0 bg-slate-50/60"></th>
              {data.weeks.map(w => (["ca", "achats", "rh", "qualite"].map(k => (
                <th key={`${w}-${k}`} className="px-1 py-1 text-center border-l border-slate-100">{METRIC_LABEL[k].slice(0, 2)}</th>
              )))).flat()}
            </tr>
          </thead>
          <tbody>
            {filtered.map(row => {
              const isSel = selected.has(row.store_id);
              return (
                <tr key={row.store_id} data-testid={`dh-row-${row.store_id}`} className={`border-t border-slate-100 hover:bg-slate-50/50 ${isSel ? "bg-blue-50/30" : ""}`}>
                  <td className="px-2 py-1.5">
                    <input type="checkbox" checked={isSel} onChange={() => toggle(row.store_id)} className="w-3.5 h-3.5"/>
                  </td>
                  <td className="px-3 py-1.5 sticky left-0 bg-white hover:bg-slate-50/50">
                    <div className="font-semibold text-slate-900">{row.store_name}</div>
                    <div className="text-[10px] text-slate-400">{row.bm_name || "—"} · {row.dr_name || "—"}</div>
                  </td>
                  {data.weeks.map(w => {
                    const cell = row.cells[w];
                    return ["ca", "achats", "rh", "qualite"].map(k => (
                      <td key={`${w}-${k}`} className="px-1 py-1.5 text-center border-l border-slate-100">
                        {cell[k]
                          ? <span className="inline-block w-4 h-4 rounded-full bg-emerald-500" title="Présent"/>
                          : <span className="inline-block w-4 h-4 rounded-full bg-rose-500" title="Manquant"/>}
                      </td>
                    ));
                  })}
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={2 + data.weeks.length * 4} className="text-center py-10 text-slate-400 italic text-sm">Aucun magasin.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-4 text-[11px] text-slate-500">
        <div className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-emerald-500"/> Donnée présente</div>
        <div className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-rose-500"/> Donnée manquante</div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  WORKFLOWS
// ═══════════════════════════════════════════════════════════════════════════
const METRIC_OPTIONS = [
  { value: "foodcost", label: "Ratio Foodcost (%)" },
  { value: "casse", label: "Casse (%)" },
  { value: "ca_realisation", label: "Réalisation CA (%)" },
  { value: "rh_etp", label: "ETP moyens" },
  { value: "hygiene_score", label: "Score hygiène (/20)" },
];
const OPERATORS = { gt: ">", gte: "≥", lt: "<", lte: "≤", eq: "=" };
const PRIORITY_COLOR = { low: "bg-slate-100 text-slate-700", medium: "bg-amber-100 text-amber-800", high: "bg-orange-100 text-orange-800", critical: "bg-rose-100 text-rose-800" };

export function WorkflowsView() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [editing, setEditing] = useState(null);
  const [running, setRunning] = useState(false);
  const [form, setForm] = useState(defaultRule());

  function defaultRule() {
    return { name: "", description: "", metric: "foodcost", operator: "gt", threshold: 28, duration_hours: 48, action: "create_issue", issue_category: "achat", issue_priority: "high", target_roles: ["manager", "director"], active: true };
  }

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/workflows"); setRules(data.items || []); }
    catch { toast.error("Chargement impossible"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(defaultRule()); setModal("rule"); };
  const openEdit = (r) => { setEditing(r); setForm({...r, target_roles: r.target_roles || []}); setModal("rule"); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await api.patch(`/admin/workflows/${editing.id}`, form); toast.success("Règle modifiée"); }
      else { await api.post("/admin/workflows", form); toast.success("Règle créée"); }
      setModal(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggle = async (r) => {
    try { await api.patch(`/admin/workflows/${r.id}`, { active: !r.active }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (r) => {
    if (!confirm(`Supprimer « ${r.name} » ?`)) return;
    try { await api.delete(`/admin/workflows/${r.id}`); toast.success("Supprimée"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const evaluate = async () => {
    if (!confirm("Évaluer toutes les règles actives et créer les problématiques correspondantes ?")) return;
    setRunning(true);
    try {
      const { data } = await api.post("/admin/workflows/evaluate");
      toast.success(`${data.rules_evaluated} règles évaluées · ${data.issues_created} problématique(s) créée(s)`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setRunning(false);
  };

  return (
    <div className="space-y-4" data-testid="workflows-view">
      <div className="bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-200 rounded-2xl p-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white grid place-items-center"><Radar className="w-5 h-5"/></div>
          <div>
            <h3 className="font-display font-bold text-slate-900">Moteur d'automatisations</h3>
            <p className="text-xs text-slate-600 mt-0.5">Règles « Si / Alors » qui créent automatiquement des problématiques et alertes. Attention à ne pas mettre des seuils trop bas — vous risquez de noyer vos équipes.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={evaluate} disabled={running} data-testid="wf-evaluate"
                  className="px-3 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold flex items-center gap-1 disabled:opacity-40">
            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Play className="w-3.5 h-3.5"/>}
            Évaluer maintenant
          </button>
          <button onClick={openCreate} data-testid="wf-add"
                  className="px-3 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold flex items-center gap-1">
            <Plus className="w-3.5 h-3.5"/> Nouvelle règle
          </button>
        </div>
      </div>

      {loading && <div className="py-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline"/></div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rules.map(r => {
          const metricLabel = METRIC_OPTIONS.find(m => m.value === r.metric)?.label || r.metric;
          return (
            <div key={r.id} data-testid={`wf-rule-${r.id}`}
                 className={`bg-white border-2 rounded-2xl p-4 ${r.active ? "border-slate-200" : "border-slate-100 opacity-60"}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-display font-bold text-slate-900">{r.name}</h4>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${PRIORITY_COLOR[r.issue_priority] || "bg-slate-100 text-slate-700"}`}>{r.issue_priority}</span>
                    {!r.active && <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-full">Inactive</span>}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{r.description || "—"}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => toggle(r)} className={`w-8 h-8 rounded-lg grid place-items-center ${r.active ? "text-emerald-600 hover:bg-emerald-50" : "text-slate-400 hover:bg-slate-100"}`}>
                    <Check className="w-4 h-4"/>
                  </button>
                  <button onClick={() => openEdit(r)} className="w-8 h-8 rounded-lg text-slate-500 hover:bg-slate-100 grid place-items-center"><Edit3 className="w-4 h-4"/></button>
                  <button onClick={() => del(r)} className="w-8 h-8 rounded-lg text-rose-500 hover:bg-rose-50 grid place-items-center"><Trash2 className="w-4 h-4"/></button>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap text-xs">
                <span className="text-slate-500 font-semibold">SI</span>
                <span className="px-2 py-1 rounded-lg bg-blue-50 text-blue-800 border border-blue-200 font-mono font-bold">{metricLabel}</span>
                <span className="font-bold text-slate-700">{OPERATORS[r.operator]}</span>
                <span className="px-2 py-1 rounded-lg bg-amber-50 text-amber-800 border border-amber-200 font-mono font-bold">{r.threshold}</span>
                <span className="text-slate-500">sur</span>
                <span className="px-2 py-1 rounded-lg bg-slate-100 text-slate-700 font-bold">{r.duration_hours}h</span>
              </div>
              <div className="mt-2 flex items-center gap-2 flex-wrap text-xs">
                <span className="text-slate-500 font-semibold">ALORS</span>
                <span className="px-2 py-1 rounded-lg bg-emerald-50 text-emerald-800 border border-emerald-200 font-semibold">
                  {r.action === "create_issue" ? "Créer une problématique" : r.action === "notify" ? "Envoyer une notification" : r.action}
                </span>
                {r.issue_category && <span className="px-2 py-1 rounded-lg bg-purple-50 text-purple-800 border border-purple-200 font-semibold">{r.issue_category}</span>}
              </div>
            </div>
          );
        })}
        {rules.length === 0 && !loading && (
          <div className="col-span-full bg-white border border-dashed border-slate-200 rounded-2xl p-14 text-center">
            <Radar className="w-8 h-8 text-slate-300 mx-auto"/>
            <div className="mt-2 text-sm text-slate-500">Aucune règle — ajoutez-en une pour commencer.</div>
          </div>
        )}
      </div>

      {modal === "rule" && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm grid place-items-center p-4" onClick={() => setModal(null)}>
          <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-xl border border-slate-200 shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="font-display text-xl font-semibold">{editing ? "Modifier la règle" : "Nouvelle règle"}</h2>
              <button type="button" onClick={() => setModal(null)} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100"><X className="w-4 h-4"/></button>
            </div>
            <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
              <input required placeholder="Nom de la règle" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="wf-name" className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm"/>
              <input placeholder="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm"/>

              <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500">SI (condition)</div>
                <div className="grid grid-cols-3 gap-2">
                  <SearchSelect value={form.metric} onChange={v => setForm({...form, metric: v})} options={[...METRIC_OPTIONS.map(m => ({ value: m.value, label: m.label }))]} size="lg" menuWidth={260}/>
                  <SearchSelect value={form.operator} onChange={v => setForm({...form, operator: v})} options={Object.entries(OPERATORS).map(([k, l]) => ({ value: k, label: l }))} size="lg" menuWidth={200}/>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-xs font-semibold text-slate-600">Seuil</label>
                  <input type="number" step="0.1" required value={form.threshold} onChange={e => setForm({...form, threshold: Number(e.target.value)})} className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm"/>
                  <label className="text-xs font-semibold text-slate-600 ml-2">Sur</label>
                  <input type="number" step="1" min="1" required value={form.duration_hours} onChange={e => setForm({...form, duration_hours: Number(e.target.value)})} className="w-24 px-3 py-2 rounded-xl border border-slate-200 text-sm"/>
                  <span className="text-xs text-slate-500">heures</span>
                </div>
              </div>

              <div className="bg-emerald-50 rounded-xl p-4 space-y-3">
                <div className="text-[11px] font-bold uppercase tracking-widest text-emerald-700">ALORS (action)</div>
                <SearchSelect value={form.action} onChange={v => setForm({...form, action: v})} options={[{ value: "create_issue", label: "Créer une problématique" }, { value: "notify", label: "Envoyer une notification uniquement" }]} size="lg" menuWidth={260}/>
                <div className="grid grid-cols-2 gap-2">
                  <SearchSelect value={form.issue_category} onChange={v => setForm({...form, issue_category: v})} options={[{ value: "achat", label: "Achat" }, { value: "rh", label: "RH" }, { value: "qualite", label: "Qualité" }, { value: "hygiene", label: "Hygiène" }, { value: "maintenance", label: "Maintenance" }]} size="lg" menuWidth={260}/>
                  <SearchSelect value={form.issue_priority} onChange={v => setForm({...form, issue_priority: v})} options={[{ value: "low", label: "Faible" }, { value: "medium", label: "Moyenne" }, { value: "high", label: "Haute" }, { value: "critical", label: "Critique" }]} size="lg" menuWidth={260}/>
                </div>
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={e => setForm({...form, active: e.target.checked})} className="w-4 h-4"/>
                <span className="text-sm text-slate-700 font-semibold">Règle active</span>
              </label>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
              <button type="button" onClick={() => setModal(null)} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">Annuler</button>
              <button type="submit" data-testid="wf-submit" className="px-4 py-2 rounded-xl text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white">Enregistrer</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  FLASH MISSIONS
// ═══════════════════════════════════════════════════════════════════════════
export function FlashMissionsView({ sectors }) {
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", deadline: "", scope: "all", target_ids: [], photo_required: true });

  const regions = useMemo(() => Array.from(new Set((sectors || []).map(s => s.region).filter(Boolean))).sort(), [sectors]);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/flash-missions"); setMissions(data.items || []); }
    catch { toast.error("Chargement impossible"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm({ title: "", description: "", deadline: "", scope: "all", target_ids: [], photo_required: true }); setModal("create"); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/flash-missions", form);
      toast.success(`Mission créée · ${(data.target_store_ids || []).length} magasins ciblés`);
      setModal(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (m) => {
    if (!confirm(`Supprimer la mission « ${m.title} » ?`)) return;
    try { await api.delete(`/admin/flash-missions/${m.id}`); toast.success("Supprimée"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openDetail = async (m) => {
    try { const { data } = await api.get(`/admin/flash-missions/${m.id}`); setDetail(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="flash-missions-view">
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-2xl p-5 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 text-white grid place-items-center"><Zap className="w-5 h-5"/></div>
          <div>
            <h3 className="font-display font-bold text-slate-900">Missions Flash</h3>
            <p className="text-xs text-slate-600 mt-0.5">Missions ponctuelles envoyées par la Direction (N1). Photo de validation obligatoire, suivi en temps réel.</p>
          </div>
        </div>
        <button onClick={openCreate} data-testid="fm-add"
                className="px-3 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 hover:opacity-90 text-white text-xs font-bold flex items-center gap-1">
          <Plus className="w-3.5 h-3.5"/> Nouvelle mission flash
        </button>
      </div>

      {loading && <div className="py-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline"/></div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {missions.map(m => {
          const pct = m.completion_pct || 0;
          const dl = m.deadline ? new Date(m.deadline) : null;
          const overdue = dl && dl < new Date();
          return (
            <button key={m.id} onClick={() => openDetail(m)} data-testid={`fm-mission-${m.id}`}
                    className="text-left bg-white border border-slate-200 rounded-2xl p-4 hover:shadow-md hover:border-slate-300 transition-all">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <h4 className="font-display font-bold text-slate-900 truncate">{m.title}</h4>
                  <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{m.description || "—"}</p>
                </div>
                <button onClick={(e) => { e.stopPropagation(); del(m); }} className="w-7 h-7 rounded-lg text-rose-500 hover:bg-rose-50 grid place-items-center"><Trash2 className="w-3.5 h-3.5"/></button>
              </div>
              <div className="flex items-center gap-2 mt-3 flex-wrap text-[11px]">
                <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-semibold">{m.scope === "all" ? "Tous corners" : m.scope === "region" ? `Région${(m.target_ids || []).length > 1 ? "s" : ""}` : "Secteur"}</span>
                {m.photo_required && <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 font-semibold flex items-center gap-1"><Camera className="w-3 h-3"/> photo obligatoire</span>}
                {dl && <span className={`px-2 py-0.5 rounded-full font-semibold flex items-center gap-1 ${overdue ? "bg-rose-100 text-rose-800" : "bg-slate-100 text-slate-700"}`}><Timer className="w-3 h-3"/> {dl.toLocaleDateString("fr-FR")}</span>}
              </div>
              <div className="mt-3">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="font-semibold text-slate-700">Exécution</span>
                  <span className="font-bold text-slate-900">{m.completion_done}/{m.completion_total} · {pct}%</span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className={`h-full ${pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-rose-500"}`} style={{width: `${pct}%`}}/>
                </div>
              </div>
            </button>
          );
        })}
        {missions.length === 0 && !loading && (
          <div className="col-span-full bg-white border border-dashed border-slate-200 rounded-2xl p-14 text-center">
            <Zap className="w-8 h-8 text-slate-300 mx-auto"/>
            <div className="mt-2 text-sm text-slate-500">Aucune mission flash — lancez-en une !</div>
          </div>
        )}
      </div>

      {/* Create modal */}
      {modal === "create" && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm grid place-items-center p-4" onClick={() => setModal(null)}>
          <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-xl border border-slate-200 shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="font-display text-xl font-semibold">Nouvelle mission flash</h2>
              <button type="button" onClick={() => setModal(null)} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100"><X className="w-4 h-4"/></button>
            </div>
            <div className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
              <input required placeholder="Titre de la mission" value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="fm-title" className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold"/>
              <textarea required placeholder="Description détaillée…" value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={4} data-testid="fm-desc" className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm"/>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Deadline</label>
                <input type="date" value={form.deadline} onChange={e => setForm({...form, deadline: e.target.value})} data-testid="fm-deadline" className="w-full mt-1 px-3 py-2.5 rounded-xl border border-slate-200 text-sm"/>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Cible</label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {[["all", "Tous les corners", Store], ["region", "Par région", Target], ["sector", "Par secteur", Filter]].map(([k, l, Ic]) => (
                    <button key={k} type="button" onClick={() => setForm({...form, scope: k, target_ids: []})} data-testid={`fm-scope-${k}`}
                            className={`p-3 rounded-xl border-2 text-left transition-all ${form.scope === k ? "border-purple-600 bg-purple-50" : "border-slate-200 hover:border-slate-300"}`}>
                      <Ic className="w-4 h-4 text-slate-600"/>
                      <div className="text-xs font-bold text-slate-800 mt-1">{l}</div>
                    </button>
                  ))}
                </div>
                {form.scope === "region" && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {regions.map(r => {
                      const on = form.target_ids.includes(r);
                      return (
                        <button key={r} type="button" onClick={() => setForm(f => ({...f, target_ids: on ? f.target_ids.filter(x => x !== r) : [...f.target_ids, r]}))}
                                className={`text-xs px-2 py-1 rounded-lg border font-semibold ${on ? "bg-purple-600 text-white border-purple-600" : "bg-white text-slate-600 border-slate-200"}`}>
                          {r}
                        </button>
                      );
                    })}
                  </div>
                )}
                {form.scope === "sector" && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {(sectors || []).map(s => {
                      const on = form.target_ids.includes(s.id);
                      return (
                        <button key={s.id} type="button" onClick={() => setForm(f => ({...f, target_ids: on ? f.target_ids.filter(x => x !== s.id) : [...f.target_ids, s.id]}))}
                                className={`text-xs px-2 py-1 rounded-lg border font-semibold ${on ? "bg-purple-600 text-white border-purple-600" : "bg-white text-slate-600 border-slate-200"}`}>
                          {s.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <label className="flex items-center gap-2 cursor-pointer p-3 rounded-xl border border-slate-200 hover:bg-slate-50">
                <input type="checkbox" checked={form.photo_required} onChange={e => setForm({...form, photo_required: e.target.checked})} data-testid="fm-photo-required" className="w-4 h-4"/>
                <Camera className="w-4 h-4 text-slate-600"/>
                <span className="text-sm text-slate-700 font-semibold">Photo de validation obligatoire</span>
              </label>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
              <button type="button" onClick={() => setModal(null)} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">Annuler</button>
              <button type="submit" data-testid="fm-submit" className="px-4 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-purple-600 to-pink-600 hover:opacity-90 text-white">Envoyer la mission</button>
            </div>
          </form>
        </div>
      )}

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm grid place-items-center p-4" onClick={() => setDetail(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-4xl border border-slate-200 shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div>
                <h2 className="font-display text-xl font-semibold">{detail.title}</h2>
                <p className="text-xs text-slate-500 mt-0.5">{detail.description}</p>
              </div>
              <button onClick={() => setDetail(null)} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100"><X className="w-4 h-4"/></button>
            </div>
            <div className="p-6 flex-1 overflow-y-auto space-y-4">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="px-2 py-1 rounded-lg bg-slate-100 text-slate-700 text-xs font-bold">{detail.scope === "all" ? "Tous corners" : detail.scope}</span>
                {detail.deadline && <span className="px-2 py-1 rounded-lg bg-slate-100 text-slate-700 text-xs font-bold"><Timer className="w-3 h-3 inline"/> {new Date(detail.deadline).toLocaleDateString("fr-FR")}</span>}
                <span className="px-2 py-1 rounded-lg bg-emerald-100 text-emerald-800 text-xs font-bold">{(detail.responses || []).filter(r => r.status === "done").length}/{(detail.target_store_ids || []).length} exécutées</span>
              </div>
              <div>
                <h3 className="font-display font-bold text-slate-900 mb-2 flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600"/> Réponses ({(detail.responses || []).length})</h3>
                {(detail.responses || []).length === 0 ? (
                  <div className="text-center py-6 text-sm text-slate-400 italic border border-dashed border-slate-200 rounded-xl">Aucune réponse encore.</div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {detail.responses.map(r => (
                      <div key={r.id} className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
                        {r.photo_url ? (
                          <a href={r.photo_url} target="_blank" rel="noreferrer" className="block aspect-video bg-slate-200 relative">
                            <img src={r.photo_url} alt={r.store_name} className="w-full h-full object-cover"/>
                          </a>
                        ) : (
                          <div className="aspect-video bg-slate-200 grid place-items-center text-slate-400"><ImageIcon className="w-8 h-8"/></div>
                        )}
                        <div className="p-2">
                          <div className="font-semibold text-xs text-slate-900 truncate">{r.store_name}</div>
                          <div className="text-[10px] text-slate-400">{r.user_name} · {new Date(r.submitted_at).toLocaleDateString("fr-FR")}</div>
                          {r.note && <div className="text-[11px] text-slate-600 mt-1 line-clamp-2">{r.note}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {(detail.pending || []).length > 0 && (
                <div>
                  <h3 className="font-display font-bold text-slate-900 mb-2 flex items-center gap-2"><Timer className="w-4 h-4 text-amber-600"/> En attente ({(detail.pending || []).length})</h3>
                  <div className="flex flex-wrap gap-1.5">
                    {detail.pending.slice(0, 60).map(p => (
                      <span key={p.store_id} className="text-[11px] px-2 py-1 rounded-lg bg-amber-50 text-amber-800 border border-amber-200 font-semibold">{p.store_name}</span>
                    ))}
                    {(detail.pending || []).length > 60 && <span className="text-[11px] text-slate-500 self-center">+{(detail.pending || []).length - 60} autres</span>}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
